import React, { useState, useEffect } from 'react';
import { FaFire, FaTrophy, FaCalendarAlt, FaChartLine } from 'react-icons/fa';
import axios from 'axios';
import './StreakTracker.css';
import config from '../../config';
const API_BASE_URL = `${config.API_BASE_URL}/api/study-tools`;

const StreakTracker = () => {
  const [currentStreak, setCurrentStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [studyDays, setStudyDays] = useState([]);
  const [motivationalQuote, setMotivationalQuote] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const quotes = [
    "Success is not final, failure is not fatal: it is the courage to continue that counts.",
    "The secret of getting ahead is getting started.",
    "Don't watch the clock; do what it does. Keep going.",
    "The only way to do great work is to love what you do.",
    "Success is the sum of small efforts, repeated day in and day out."
  ];

  useEffect(() => {
    fetchStreakData();
  }, []);

  const fetchStreakData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get analytics data which includes streak information
      const analyticsResponse = await axios.get(`${API_BASE_URL}/analytics`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      const { currentStreak: current, longestStreak: longest } = analyticsResponse.data;
      setCurrentStreak(current);
      setLongestStreak(longest);

      // Get study history from streak data
      const today = new Date();
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const days = [];
      for (let d = new Date(thirtyDaysAgo); d <= today; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        days.push({
          date: dateStr,
          studied: analyticsResponse.data.studySessions.some(
            session => new Date(session.date).toISOString().split('T')[0] === dateStr
          )
        });
      }
      setStudyDays(days);

      // Set random motivational quote
      setMotivationalQuote(quotes[Math.floor(Math.random() * quotes.length)]);
    } catch (error) {
      console.error('Error fetching streak data:', error);
      setError('Failed to load streak data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getMotivationalMessage = () => {
    if (currentStreak === 0) {
      return "Start your streak today!";
    } else if (currentStreak < 3) {
      return "Great start! Keep going!";
    } else if (currentStreak < 7) {
      return "You're building momentum!";
    } else if (currentStreak < 14) {
      return "Impressive streak! Don't break the chain!";
    } else {
      return "You're unstoppable! Amazing work!";
    }
  };

  const getDayClass = (day) => {
    const today = new Date().toISOString().split('T')[0];
    const classes = ['calendar-day'];
    
    if (day.studied) {
      classes.push('active');
    }
    if (day.date === today) {
      classes.push('today');
    }
    
    return classes.join(' ');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="streak-tracker">
        <div className="loading-spinner">Loading streak data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="streak-tracker">
        <div className="error-message">
          {error}
          <button onClick={fetchStreakData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="streak-tracker">
      <div className="streak-header">
        <h2>Study Streak</h2>
        <div className="motivational-quote">
          "{motivationalQuote}"
        </div>
      </div>

      <div className="streak-stats">
        <div className="stat-card">
          <FaFire className="stat-icon current" />
          <div className="stat-info">
            <h3>Current Streak</h3>
            <div className="stat-value">{currentStreak} days</div>
          </div>
          <div className="stat-message">{getMotivationalMessage()}</div>
        </div>

        <div className="stat-card">
          <FaTrophy className="stat-icon best" />
          <div className="stat-info">
            <h3>Longest Streak</h3>
            <div className="stat-value">{longestStreak} days</div>
          </div>
        </div>
      </div>

      <div className="streak-calendar-section">
        <h3><FaCalendarAlt /> Study Calendar</h3>
        <div className="streak-calendar">
          {studyDays.map((day, index) => (
            <div 
              key={day.date}
              className={getDayClass(day)}
              title={`${formatDate(day.date)}${day.studied ? ' - Studied' : ' - No study'}`}
            >
              {new Date(day.date).getDate()}
            </div>
          ))}
        </div>
      </div>

      <div className="streak-insights">
        <h3><FaChartLine /> Insights</h3>
        <div className="insights-grid">
          <div className="insight-card">
            <h4>Study Rate</h4>
            <div className="insight-value">
              {Math.round((studyDays.filter(day => day.studied).length / studyDays.length) * 100)}%
            </div>
            <p>of days this month</p>
          </div>

          <div className="insight-card">
            <h4>Best Streak</h4>
            <div className="insight-value">{longestStreak}</div>
            <p>consecutive days</p>
          </div>

          <div className="insight-card">
            <h4>Current Progress</h4>
            <div className="insight-value">
              {Math.round((currentStreak / longestStreak) * 100) || 0}%
            </div>
            <p>of best streak</p>
          </div>
        </div>
      </div>

      <div className="streak-motivation">
        <h3>Keep the Momentum</h3>
        <div className="motivation-tips">
          <div className="tip">
            <strong>Tip:</strong> Study at the same time each day to build a routine
          </div>
          <div className="tip">
            <strong>Remember:</strong> Even 30 minutes of focused study counts
          </div>
          <div className="tip">
            <strong>Goal:</strong> Can you beat your longest streak of {longestStreak} days?
          </div>
        </div>
      </div>
    </div>
  );
};

export default StreakTracker;